import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type { User } from '../../../types';
import type {
  ChangeExtraFields,
  ChangeExtraValue,
  ChangeFormField,
  ChangeRequest,
  ChangeWorkGroup,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { validateAttachmentFile } from '../../../utils/uploadValidation';
import {
  buildExtraFieldsPayload,
  changeOwnerAllowsSelection,
  hydrateExtraFields,
  validateExtraFields,
  validateNativeRequired,
} from '../changeFormSchema';
import { augmentWorkGroupsWithCatalogTemplates } from '../changeAugmentWorkGroupsWithTemplates';
import {
  canAddReviewArea,
  flattenInternalSlots,
  internalReviewKey,
  internalSlotsForReviewKeys,
  resolveInternalSlotByReviewKey,
  internalSlotLabel,
  isCreateReviewExternal,
  isCreateReviewExternalSuggested,
  isCreateReviewInternal,
  leftoverGroups,
  leftoverManualInternalSlots,
  parseExternalLineKey,
  parseInternalReviewKey,
  resolveReviewIncidentWorkflow,
  slotReviewKey,
} from '../changeCreateReview';
import {
  computeCreateSubtaskReviewPermissions,
  deniedSubtaskRequesterPolicy,
  subtaskRequesterPolicyFromFormSchema,
  type SubtaskRequesterPolicy,
} from '../changeSubtaskCreateReviewPermissions';
import {
  buildChangeFormPayload,
  formSkipsAutoChecklist,
  validateChangeForm,
  type ChangeFormValues,
} from '../changeValidation';
import { validateImmediateManagerPickForSubmit } from '../changeImmediateManagerValidation';
import { useChangeCatalogs } from '../hooks/useChangeCatalogs';
import { useChangeFormUsers } from '../hooks/useChangeFormUsers';
import { useChangeTypePolicies } from '../hooks/useChangeTypePolicies';
import { emptyIncidentDraft, incidentAllowsAttachments, incidentCustomFieldsPayload } from '../incidentQuickSend';
import {
  hydrateIncidentCustomFieldValues,
  uploadIncidentCustomFieldFiles,
  validateIncidentTicketFields,
} from '../incidentTicketFields';
import ChangeConfigurableForm from './ChangeConfigurableForm';
import { parseQuestionOptions } from '../changeInstanceChecklistFields';
import ChangeCreateSubtaskReview, {
  type ExternalReviewDraft,
  type InternalReviewDraft,
} from './ChangeCreateSubtaskReview';
import ChangeFormFields from './ChangeFormFields';

interface Props {
  show: boolean;
  onHide: () => void;
  onCreated: () => Promise<void> | void;
  users: User[];
  isAdmin: boolean;
  currentUserId?: number;
  canChooseRequester?: boolean;
}

interface CreateFormState extends ChangeFormValues {
  change_owner_id: number;
  requester_id: number;
}

type IncidentDraft = ExternalReviewDraft;

interface IncidentTargets {
  groups: Array<{ id: number; name: string }>;
  workflows: Array<{ id: number; name: string; group_ids: number[] }>;
}

const initialForm = (): CreateFormState => ({
  title: '',
  description: '',
  scope: '',
  impacted_users: '',
  go_live_start_at: '',
  go_live_end_at: '',
  risk_committee_start_at: '',
  risk_committee_end_at: '',
  camud_go_no_go_date: '',
  camud_extraordinary: false,
  rollback_plan: '',
  change_type: 'normal',
  change_owner_id: 0,
  requester_id: 0,
  requester_area_id: 0,
  requester_manager_group_id: 0,
  project: '',
  category_id: 0,
  sap_classification: '',
  sap_has_request: null,
  task_type_ids: [],
});

const emptyDraft = (
  group: ChangeWorkGroup,
  workflows: IncidentTargets['workflows'] = [],
): IncidentDraft => {
  const base = emptyIncidentDraft(group);
  const workflowId = resolveReviewIncidentWorkflow(group, workflows) || base.workflow_id;
  return {
    selected: true,
    ...base,
    workflow_id: workflowId,
  };
};

const INTERNAL_QUESTION_FIELD_TYPES = new Set([
  'text_short',
  'text_long',
  'integer',
  'decimal',
  'date',
  'datetime',
  'period',
  'period_datetime',
  'list',
  'yes_no',
]);

const emptyInternalDraft = (group: ChangeWorkGroup): InternalReviewDraft => ({
  selected: true,
  title: (group.internal_subtask_template_name || group.name || '').trim() || 'Checklist',
  comments: '',
  task_type_id: '',
  assignment_kind: 'setor',
  assignee_id: '',
  questions: [],
});

const ChangeCreateModal: React.FC<Props> = ({
  show,
  onHide,
  onCreated,
  users,
  isAdmin,
  currentUserId,
  canChooseRequester = true,
}) => {
  const [form, setForm] = useState<CreateFormState>(initialForm());
  const [forceOutside, setForceOutside] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schema, setSchema] = useState<ChangeFormField[]>([]);
  const [extras, setExtras] = useState<ChangeExtraFields>({});
  const [step, setStep] = useState<1 | 2>(1);
  const [workGroups, setWorkGroups] = useState<ChangeWorkGroup[]>([]);
  const [incidentTargets, setIncidentTargets] = useState<IncidentTargets>({ groups: [], workflows: [] });
  const [drafts, setDrafts] = useState<Record<number, IncidentDraft>>({});
  const [internalDrafts, setInternalDrafts] = useState<Record<string, InternalReviewDraft>>({});
  const [reviewInternalKeys, setReviewInternalKeys] = useState<string[]>([]);
  const [reviewExternalIds, setReviewExternalIds] = useState<number[]>([]);
  const [reviewEditing, setReviewEditing] = useState(false);
  const [createdChange, setCreatedChange] = useState<ChangeRequest | null>(null);
  const [groupsReady, setGroupsReady] = useState(false);
  const [subtaskRequesterPolicy, setSubtaskRequesterPolicy] = useState<SubtaskRequesterPolicy>(
    deniedSubtaskRequesterPolicy(),
  );
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const { categories, requesterAreas, taskTypes } = useChangeCatalogs({ enabled: show });
  const { typeOptions, flagsFor } = useChangeTypePolicies({ enabled: show });
  const subtaskPolicy = useMemo(() => flagsFor(form.change_type), [flagsFor, form.change_type]);
  const canChooseOwner = useMemo(
    () => changeOwnerAllowsSelection(schema.find((item) => item.native_key === 'change_owner_id')),
    [schema],
  );
  const createReviewPermissions = useMemo(
    () =>
      computeCreateSubtaskReviewPermissions({
        isChangeManager: isAdmin,
        currentUserId,
        requesterId: form.requester_id,
        changeOwnerId: form.change_owner_id,
        createdBy: currentUserId,
        policy: subtaskRequesterPolicy,
      }),
    [isAdmin, currentUserId, form.requester_id, form.change_owner_id, subtaskRequesterPolicy],
  );
  const templatePersistMode = useMemo(() => {
    if (!subtaskPolicy.enable_internal_subtasks) return null;
    if (isAdmin) return 'global' as const;
    if (createReviewPermissions.canAdd) return 'personal' as const;
    return null;
  }, [subtaskPolicy.enable_internal_subtasks, isAdmin, createReviewPermissions.canAdd]);

  const attachTemplateSlotToReview = (templateId: number, groupId: number, items: ChangeWorkGroup[]) => {
    const manualKey = internalReviewKey(groupId, 0);
    const templateKey = internalReviewKey(groupId, templateId);
    const slot =
      flattenInternalSlots(items).find(
        (item) => item.group_id === groupId && item.internal_subtask_template_id === templateId,
      ) || resolveInternalSlotByReviewKey(items, templateKey);
    if (!slot) return;
    const key = slotReviewKey(slot);
    setReviewInternalKeys((prev) => {
      const withoutManual = prev.filter((existing) => {
        if (existing === manualKey) return false;
        const [g, t] = existing.split(':');
        return Number(g) !== groupId || Number(t) === templateId;
      });
      return withoutManual.includes(key) ? withoutManual : [...withoutManual, key];
    });
    setInternalDrafts((prev) => {
      const carried = prev[manualKey] || prev[key] || emptyInternalDraft(slot);
      const next = { ...prev };
      delete next[manualKey];
      return { ...next, [key]: carried };
    });
  };

  const handleTemplateSaved = (templateId: number, groupId: number) => {
    void Promise.all([
      changesApi.listWorkGroups({ only_active: true }),
      changesApi
        .listInternalSubtaskTemplates({ only_active: true, catalog_only: isAdmin })
        .catch(() => ({ items: [] })),
    ]).then(([data, catalog]) => {
      const items = augmentWorkGroupsWithCatalogTemplates(data.items || [], catalog.items || []);
      setWorkGroups(items);
      attachTemplateSlotToReview(templateId, groupId, items);
    });
  };

  const handleTemplateDeleted = (templateId: number) => {
    void changesApi.listWorkGroups({ only_active: true }).then((data) => {
      const items = data.items || [];
      setWorkGroups(items);
      setReviewInternalKeys((prev) =>
        prev.filter((key) => {
          const slot = flattenInternalSlots(items).find((item) => slotReviewKey(item) === key);
          return slot?.internal_subtask_template_id !== templateId;
        }),
      );
    });
  };

  useEffect(() => {
    if (!error || !modalBodyRef.current) return;
    modalBodyRef.current.scrollTop = 0;
  }, [error]);

  const createFields = useMemo(() => {
    const sapSlot: ChangeFormField = {
      id: -72,
      kind: 'native',
      native_key: 'sap_classification',
      label: 'SAP ou Não SAP',
      field_type: 'list',
      is_required: true,
      options: [],
      sort_order: 72,
      col_span: 6,
      is_visible: true,
      is_locked: true,
      help_text: 'Define se o redisparo de Infra vai para Hosting (SAP) ou Nuvem (Não SAP).',
    };
    const withSap = schema.some((field) => field.native_key === 'sap_classification')
      ? schema
      : (() => {
          const afterArea = schema.findIndex((field) => field.native_key === 'requester_area_id');
          if (afterArea < 0) return [...schema, sapSlot];
          return [...schema.slice(0, afterArea + 1), sapSlot, ...schema.slice(afterArea + 1)];
        })();
    return withSap.filter((field) => field.native_key !== 'task_type_ids');
  }, [schema]);

  const needsLegacyUserSelect = useMemo(
    () =>
      createFields.some(
        (field) =>
          (field.native_key === 'requester_id' || field.native_key === 'change_owner_id') &&
          field.is_visible !== false &&
          !field.select_search_enabled,
      ),
    [createFields],
  );
  const { users: loadedFormUsers } = useChangeFormUsers({
    enabled: show && needsLegacyUserSelect,
    limit: 50,
  });
  const peopleForForm = needsLegacyUserSelect ? (users.length ? users : loadedFormUsers) : [];

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    void changesApi.getFormSchema().catch(() => ({ items: [] as ChangeFormField[] })).then((formSchema) => {
      if (cancelled) return;
      const items = formSchema.items || [];
      setSchema(items);
      setExtras(hydrateExtraFields(items));
    });
    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    void changesApi
      .getSubtaskFormSchema()
      .then((data) => {
        if (cancelled) return;
        setSubtaskRequesterPolicy(subtaskRequesterPolicyFromFormSchema(data));
      })
      .catch(() => {
        if (cancelled) return;
        setSubtaskRequesterPolicy(deniedSubtaskRequesterPolicy());
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setGroupsReady(false);
    void Promise.all([
      changesApi.listWorkGroups({ only_active: true }).catch(() => ({ items: [] as ChangeWorkGroup[] })),
      changesApi
        .listInternalSubtaskTemplates({ only_active: true, catalog_only: isAdmin })
        .catch(() => ({ items: [] })),
      changesApi.listIncidentTargets().catch(() => ({ groups: [], workflows: [] })),
    ]).then(([areas, templateCatalog, targets]) => {
      if (cancelled) return;
      const items = augmentWorkGroupsWithCatalogTemplates(
        areas.items || [],
        templateCatalog.items || [],
      );
      const mapped = items.filter((item) =>
        isCreateReviewExternalSuggested(item, targets.workflows || []),
      );
      setWorkGroups(items);
      setIncidentTargets(targets || { groups: [], workflows: [] });
      setDrafts(
        Object.fromEntries(
          mapped.map((group) => [group.group_id, emptyDraft(group, targets.workflows || [])]),
        ),
      );
      setInternalDrafts(
        Object.fromEntries(
          flattenInternalSlots(items)
            .filter(
              (slot) =>
                slot.auto_subtask_on_normal && Number(slot.checklist_field_count || 0) > 0,
            )
            .map((slot) => [slotReviewKey(slot), emptyInternalDraft(slot)]),
        ),
      );
    }).finally(() => {
      if (!cancelled) setGroupsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [show, isAdmin]);

  useEffect(() => {
    if (subtaskPolicy.enable_internal_subtasks) return;
    setReviewInternalKeys([]);
    setInternalDrafts({});
  }, [form.change_type, subtaskPolicy.enable_internal_subtasks]);

  const override = { isAdmin, forceOutside, forceReason };
  const skipChecklist = formSkipsAutoChecklist(createFields, form, extras);
  const suggestedInternals = useMemo(
    () =>
      flattenInternalSlots(workGroups).filter(
        (slot) =>
          isCreateReviewInternal(slot, form.change_type, skipChecklist, subtaskPolicy) &&
          Number(slot.checklist_field_count || 0) > 0,
      ),
    [workGroups, form.change_type, skipChecklist, subtaskPolicy],
  );
  const suggestedExternals = useMemo(
    () =>
      subtaskPolicy.enable_external_subtasks
        ? workGroups.filter((group) =>
            isCreateReviewExternalSuggested(group, incidentTargets.workflows),
          )
        : [],
    [workGroups, incidentTargets.workflows, subtaskPolicy.enable_external_subtasks],
  );
  const internalGroups = useMemo(
    () => internalSlotsForReviewKeys(workGroups, reviewInternalKeys),
    [workGroups, reviewInternalKeys],
  );
  const externalGroups = useMemo(
    () => workGroups.filter((group) => reviewExternalIds.includes(group.group_id)),
    [workGroups, reviewExternalIds],
  );
  const leftoverInternals = useMemo(
    () =>
      leftoverManualInternalSlots(workGroups, reviewInternalKeys, {
        allowInternal: subtaskPolicy.enable_internal_subtasks,
        changeType: form.change_type,
        policy: subtaskPolicy,
      }),
    [workGroups, reviewInternalKeys, form.change_type, subtaskPolicy],
  );
  const leftoverInternalAreas = useMemo(() => {
    const used = new Set<number>([
      ...reviewInternalKeys.map((key) => parseInternalReviewKey(key).groupId),
      ...leftoverInternals.map((slot) => slot.group_id),
    ]);
    return leftoverGroups(workGroups, Array.from(used), canAddReviewArea);
  }, [workGroups, reviewInternalKeys, leftoverInternals]);
  const leftoverExternals = useMemo(
    () =>
      leftoverGroups(workGroups, reviewExternalIds, (group) =>
        Boolean(subtaskPolicy.enable_external_subtasks && canAddReviewArea(group)),
      ),
    [workGroups, reviewExternalIds, subtaskPolicy.enable_external_subtasks],
  );
  const hasReviewItems =
    suggestedInternals.length > 0 ||
    suggestedExternals.length > 0 ||
    leftoverInternals.length > 0 ||
    leftoverInternalAreas.length > 0 ||
    leftoverExternals.length > 0 ||
    reviewInternalKeys.length > 0 ||
    reviewExternalIds.length > 0;
  const selectedGroups = useMemo(
    () => externalGroups.filter((group) => drafts[group.group_id]?.selected),
    [externalGroups, drafts],
  );
  const selectedInternals = useMemo(
    () =>
      internalGroups.filter((group) => internalDrafts[slotReviewKey(group)]?.selected !== false),
    [internalGroups, internalDrafts],
  );
  const selectedReviewCount = selectedInternals.length + selectedGroups.length;

  useEffect(() => {
    if (!show || !currentUserId) return;
    setError(null);
    setForm((prev) => {
      const nextRequester = prev.requester_id || currentUserId;
      const nextOwner = canChooseOwner ? prev.change_owner_id || currentUserId : currentUserId;
      if (prev.requester_id === nextRequester && prev.change_owner_id === nextOwner) return prev;
      return { ...prev, requester_id: nextRequester, change_owner_id: nextOwner };
    });
  }, [show, currentUserId, canChooseOwner]);

  const handleFormChange = (patch: Partial<ChangeFormValues>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleOverrideChange = (patch: { forceOutside?: boolean; forceReason?: string }) => {
    if (patch.forceOutside !== undefined) setForceOutside(patch.forceOutside);
    if (patch.forceReason !== undefined) setForceReason(patch.forceReason);
  };

  const findGroup = (groupId: number) => workGroups.find((item) => item.group_id === groupId);

  const patchDraft = (lineKey: string, patch: Partial<IncidentDraft>) => {
    const groupId = parseExternalLineKey(lineKey).groupId;
    if (!groupId) return;
    const group = findGroup(groupId);
    if (!group) return;
    setDrafts((prev) => ({
      ...prev,
      [groupId]: { ...(prev[groupId] || emptyDraft(group, incidentTargets.workflows)), ...patch },
    }));
  };

  const patchInternalDraft = (reviewKey: string, patch: Partial<InternalReviewDraft>) => {
    const slot = resolveInternalSlotByReviewKey(workGroups, reviewKey);
    if (!slot) return;
    setInternalDrafts((prev) => ({
      ...prev,
      [reviewKey]: { ...(prev[reviewKey] || emptyInternalDraft(slot)), ...patch },
    }));
  };

  const addInternalToReview = (group: ChangeWorkGroup) => {
    const key = slotReviewKey(group);
    setInternalDrafts((prev) => ({
      ...prev,
      [key]: prev[key] || emptyInternalDraft(group),
    }));
    setReviewInternalKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    return key;
  };

  const addExternalToReview = (group: ChangeWorkGroup) => {
    setDrafts((prev) => ({
      ...prev,
      [group.group_id]: prev[group.group_id] || emptyDraft(group, incidentTargets.workflows),
    }));
    setReviewExternalIds((prev) => (prev.includes(group.group_id) ? prev : [...prev, group.group_id]));
    return String(group.group_id);
  };

  const convertInternalToExternal = (group: ChangeWorkGroup) => {
    setReviewInternalKeys((prev) => prev.filter((key) => key !== slotReviewKey(group)));
    addExternalToReview(group);
  };

  const seedReviewLists = () => {
    setReviewEditing(false);
    setReviewInternalKeys((prev) => {
      const suggested = suggestedInternals.map((slot) => slotReviewKey(slot));
      const extra = prev.filter((key) => {
        if (suggested.includes(key)) return false;
        const slot = flattenInternalSlots(workGroups).find((item) => slotReviewKey(item) === key);
        return Boolean(slot && canAddReviewArea(slot));
      });
      return [...suggested, ...extra];
    });
    const leftoverExt = workGroups.filter(
      (group) =>
        canAddReviewArea(group) &&
        isCreateReviewExternal(group, incidentTargets.workflows) &&
        !suggestedExternals.some((item) => item.group_id === group.group_id),
    );
    setReviewExternalIds((prev) => {
      const suggested = suggestedExternals.map((group) => group.group_id);
      const leftoverIds = leftoverExt.map((group) => group.group_id);
      const extra = prev.filter(
        (id) =>
          !suggested.includes(id) &&
          !leftoverIds.includes(id) &&
          workGroups.some((group) => group.group_id === id && canAddReviewArea(group)),
      );
      return [...suggested, ...leftoverIds, ...extra];
    });
    setDrafts((prev) => {
      const next = { ...prev };
      leftoverExt.forEach((group) => {
        next[group.group_id] = next[group.group_id] || emptyDraft(group, incidentTargets.workflows);
      });
      return next;
    });
  };

  const resetForm = () => {
    setForm({
      ...initialForm(),
      requester_id: currentUserId || 0,
      change_owner_id: currentUserId || 0,
    });
    setForceOutside(false);
    setForceReason('');
    setExtras(hydrateExtraFields(schema));
    setStep(1);
    setCreatedChange(null);
    setReviewEditing(false);
    setReviewInternalKeys([]);
    setReviewExternalIds([]);
    setDrafts(
      Object.fromEntries(
        suggestedExternals.map((group) => [group.group_id, emptyDraft(group, incidentTargets.workflows)]),
      ),
    );
    setInternalDrafts(
      Object.fromEntries(
        suggestedInternals.map((slot) => [slotReviewKey(slot), emptyInternalDraft(slot)]),
      ),
    );
  };

  const validateStepOne = async (): Promise<boolean> => {
    setError(null);
    if (!form.change_owner_id || !form.requester_id) {
      setError('Selecione Dono da Mudança e Solicitante.');
      return false;
    }
    const validationError = validateChangeForm(form, override, createFields, extras);
    if (validationError) {
      setError(validationError);
      return false;
    }
    const immediateManagerError = await validateImmediateManagerPickForSubmit(
      form,
      createFields,
      extras,
      canChooseRequester ? form.requester_id : currentUserId || form.requester_id,
    );
    if (immediateManagerError) {
      setError(immediateManagerError);
      return false;
    }
    const nativeError = validateNativeRequired(createFields, form, extras);
    if (nativeError) {
      setError(nativeError);
      return false;
    }
    const extraError = validateExtraFields(createFields, form, extras);
    if (extraError) {
      setError(extraError);
      return false;
    }
    return true;
  };

  const resolveExternalDraft = (group: ChangeWorkGroup): IncidentDraft =>
    drafts[group.group_id] ?? emptyDraft(group, incidentTargets.workflows);

  const positiveIntOrUndefined = (value: number | '' | undefined): number | undefined => {
    if (typeof value === 'number' && value > 0) return value;
    return undefined;
  };

  const validatePackage = async (): Promise<Record<number, IncidentDraft> | null> => {
    setError(null);
    const preparedExternals: Record<number, IncidentDraft> = {};
    for (const group of selectedInternals) {
      const draft = internalDrafts[slotReviewKey(group)] ?? emptyInternalDraft(group);
      const label = internalSlotLabel(group);
      if (!(draft.title || group.internal_subtask_template_name || group.name || '').trim()) {
        setError(`Informe o título da subtarefa de ${label}.`);
        return null;
      }
      if (draft.assignment_kind === 'pessoa' && !positiveIntOrUndefined(draft.assignee_id)) {
        setError(`Escolha a pessoa da subtarefa de ${label}.`);
        return null;
      }
      for (const question of draft.questions || []) {
        if (!question.label.trim()) {
          setError(`Há pergunta sem enunciado na subtarefa de ${label}.`);
          return null;
        }
        if (!INTERNAL_QUESTION_FIELD_TYPES.has(question.field_type)) {
          setError(
            `${label}: o tipo de pergunta «${question.field_type}» não pode ser enviado na criação. ` +
              'Use Editar → Perguntas e campos (gravado no cadastro da área) ou troque o tipo.',
          );
          return null;
        }
        if (question.field_type === 'list' && parseQuestionOptions(question.options).length < 2) {
          setError(`A lista da subtarefa de ${label} precisa de ao menos duas opções.`);
          return null;
        }
      }
    }

    const draftPatches: Record<number, Partial<IncidentDraft>> = {};
    for (const group of selectedGroups) {
      let draft = resolveExternalDraft(group);
      const workflowId = Number(
        draft.workflow_id || resolveReviewIncidentWorkflow(group, incidentTargets.workflows) || 0,
      );
      if (!Number.isFinite(workflowId) || workflowId <= 0) {
        setError(`Escolha o fluxo de incidentes de ${group.name}.`);
        return null;
      }
      if (!draft.workflow_id) {
        draftPatches[group.group_id] = { ...(draftPatches[group.group_id] || {}), workflow_id: workflowId };
        draft = { ...draft, workflow_id: workflowId };
      }
      if (!draft.customFields?.length) {
        try {
          const fields = await changesApi.listIncidentWorkflowFields(
            workflowId,
            draft.incidents_group_id,
          );
          const mergedValues = {
            ...hydrateIncidentCustomFieldValues(group.incident_custom_field_defaults),
            ...draft.customFieldValues,
          };
          draftPatches[group.group_id] = {
            ...(draftPatches[group.group_id] || {}),
            customFields: fields,
            customFieldValues: mergedValues,
          };
          draft = { ...draft, customFields: fields, customFieldValues: mergedValues };
        } catch (err: unknown) {
          setError(
            getApiErrorMessage(
              err,
              `Não foi possível carregar os campos do chamado de ${group.name}. Abra Editar e tente de novo.`,
            ),
          );
          return null;
        }
      }
      if (!draft.incidents_group_id) {
        setError(`Escolha o grupo para encaminhar o chamado de ${group.name}.`);
        return null;
      }
      if (!(draft.title || '').trim()) {
        setError(`Informe o título do chamado de ${group.name}.`);
        return null;
      }
      if (!(draft.comments || '').trim()) {
        setError(`Informe a descrição do chamado de ${group.name}.`);
        return null;
      }
      if (!(draft.urgency || '').trim()) {
        setError(`Informe a urgência do chamado de ${group.name}.`);
        return null;
      }
      if (!incidentAllowsAttachments(group) && (draft.files || []).length) {
        setError(`O chamado de ${group.name} não aceita anexo.`);
        return null;
      }
      const customError = validateIncidentTicketFields(
        draft.customFields,
        draft.customFieldValues,
        draft.customFieldFiles,
      );
      if (customError) {
        setError(`${group.name}: ${customError}. Use Editar no chamado para preencher.`);
        return null;
      }
      const invalid = (draft.files || []).map(validateAttachmentFile).find(Boolean);
      if (invalid) {
        setError(invalid);
        return null;
      }
      preparedExternals[group.group_id] = draft;
    }

    if (Object.keys(draftPatches).length) {
      setDrafts((prev) => {
        const next = { ...prev };
        for (const [groupIdText, patch] of Object.entries(draftPatches)) {
          const groupId = Number(groupIdText);
          const group = findGroup(groupId);
          if (!group) continue;
          next[groupId] = { ...(prev[groupId] || emptyDraft(group, incidentTargets.workflows)), ...patch };
        }
        return next;
      });
    }
    return preparedExternals;
  };

  const uploadPackageFiles = async (
    change: ChangeRequest,
    preparedExternals: Record<number, IncidentDraft> = {},
  ) => {
    for (const group of selectedGroups) {
      const draft = preparedExternals[group.group_id] ?? drafts[group.group_id];
      const files = draft?.files || [];
      const customFiles = draft?.customFieldFiles || {};
      const hasCustomFiles = Object.values(customFiles).some((value) =>
        Array.isArray(value) ? value.length > 0 : Boolean(value),
      );
      if (!files.length && !hasCustomFiles) continue;
      const subtask = (change.subtasks || []).find(
        (item) => item.origin === 'incidente' && item.group_id === group.group_id,
      );
      if (!subtask) {
        throw new Error(`Subtarefa de ${group.name} não encontrada após criar a mudança.`);
      }
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await changesApi.uploadSubtaskAttachment(change.id, subtask.id, formData);
      }
      const ticketId = subtask.ticket_id || subtask.incident?.ticket_id;
      if (ticketId) {
        await uploadIncidentCustomFieldFiles(ticketId, customFiles);
      }
    }
  };

  const finishCreate = async (preparedExternals: Record<number, IncidentDraft> = {}) => {
    setSaving(true);
    try {
      let change = createdChange;
      if (!change) {
        change = await changesApi.create({
          ...buildChangeFormPayload(form, override, createFields, extras),
          change_owner_id: canChooseOwner
            ? form.change_owner_id
            : currentUserId || form.change_owner_id,
          requester_id: canChooseRequester ? form.requester_id : currentUserId || form.requester_id,
          extra_fields: buildExtraFieldsPayload(createFields, form, extras),
          checklist_group_ids: selectedInternals.map((group) => group.group_id),
          checklist_titles: Object.fromEntries(
            selectedInternals.map((group) => [
              group.group_id,
              (
                internalDrafts[slotReviewKey(group)]?.title ||
                group.internal_subtask_template_name ||
                group.name ||
                ''
              ).trim(),
            ]),
          ),
          internal_subtasks: selectedInternals.map((group) => {
            const draft = internalDrafts[slotReviewKey(group)] ?? emptyInternalDraft(group);
            return {
              group_id: group.group_id,
              template_id: group.internal_subtask_template_id || undefined,
              title: (draft.title || group.internal_subtask_template_name || group.name || '').trim(),
              comments: draft.comments?.trim() || undefined,
              task_type_id: positiveIntOrUndefined(draft.task_type_id),
              assignment_kind: draft.assignment_kind || 'setor',
              assignee_id:
                draft.assignment_kind === 'pessoa'
                  ? positiveIntOrUndefined(draft.assignee_id)
                  : undefined,
              questions: (draft?.questions || []).map((question) => ({
                label: question.label.trim(),
                field_type: question.field_type,
                is_required: question.is_required,
                help_text: question.help_text?.trim() || undefined,
                options:
                  question.field_type === 'list' || question.field_type === 'yes_no'
                    ? parseQuestionOptions(question.options)
                    : [],
              })),
            };
          }),
          incident_subtasks: selectedGroups.map((group) => {
            const draft = preparedExternals[group.group_id] ?? resolveExternalDraft(group);
            const workflowId = Number(
              draft.workflow_id || resolveReviewIncidentWorkflow(group, incidentTargets.workflows),
            );
            return {
              group_id: group.group_id,
              workflow_id: workflowId,
              incidents_group_id: draft.incidents_group_id || undefined,
              title: draft.title.trim(),
              comments: draft.comments.trim(),
              urgency: draft.urgency,
              has_attachment: (draft.files || []).length > 0,
              gates_implementar: false,
              ...incidentCustomFieldsPayload(draft),
            };
          }),
        });
        setCreatedChange(change);
      }
      await uploadPackageFiles(change, preparedExternals);
      await onCreated();
      resetForm();
      onHide();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível criar a mudança.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePrimary = async () => {
    try {
      if (step === 1) {
        if (!(await validateStepOne())) return;
        if (hasReviewItems) {
          seedReviewLists();
          setStep(2);
          return;
        }
      } else {
        const preparedExternals = await validatePackage();
        if (preparedExternals === null) return;
        await finishCreate(preparedExternals);
        return;
      }
      await finishCreate();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível concluir a criação. Tente de novo.'));
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>{step === 1 ? 'Nova Mudança' : 'Confirme as subtarefas'}</Modal.Title>
      </Modal.Header>
      <Modal.Body ref={modalBodyRef}>
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {saving ? (
          <Alert variant="info" className="py-2">
            Criando mudança e subtarefas… Aguarde alguns segundos.
          </Alert>
        ) : null}
        {step === 1 ? (
          schema.length ? (
            <>
              <ChangeConfigurableForm
                idPrefix="create"
                fields={createFields}
                form={form}
                extras={extras}
                onFormChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                onExtraChange={(fieldId, value: ChangeExtraValue) =>
                  setExtras((prev) => ({ ...prev, [String(fieldId)]: value }))
                }
                categories={categories}
                requesterAreas={requesterAreas}
                taskTypes={taskTypes}
                users={peopleForForm}
                override={override}
                onOverrideChange={handleOverrideChange}
                showPeopleFields
                showTaskTypes={false}
                canChooseRequester={canChooseRequester}
                canChooseOwner={canChooseOwner}
                typeOptions={typeOptions}
              />
            </>
          ) : (
            <ChangeFormFields
              idPrefix="create"
              form={form}
              onFormChange={handleFormChange}
              categories={categories}
              requesterAreas={requesterAreas}
              taskTypes={taskTypes}
              override={override}
              onOverrideChange={handleOverrideChange}
            >
              <Col xs={12} md={6}>
                <Form.Label>Dono da Mudança</Form.Label>
                <Form.Select
                  aria-label="Dono da Mudança"
                  value={form.change_owner_id || ''}
                  disabled={!canChooseOwner}
                  onChange={(e) => setForm({ ...form, change_owner_id: Number(e.target.value) })}
                >
                  <option value="">Selecione</option>
                  {users.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label>Solicitante</Form.Label>
                <Form.Select
                  aria-label="Solicitante"
                  value={form.requester_id || ''}
                  disabled={!canChooseRequester}
                  onChange={(e) => setForm({ ...form, requester_id: Number(e.target.value) })}
                >
                  <option value="">Selecione</option>
                  {users.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </ChangeFormFields>
          )
        ) : (
          <ChangeCreateSubtaskReview
            internals={internalGroups}
            externals={externalGroups}
            leftoverInternals={leftoverInternals}
            leftoverExternals={leftoverExternals}
            internalDrafts={internalDrafts}
            externalDrafts={drafts}
            workflows={incidentTargets.workflows}
            incidentTargetGroups={incidentTargets.groups}
            taskTypes={taskTypes}
            users={users}
            onToggleInternal={(groupId, selected) => {
              if (!createReviewPermissions.canSelect) return;
              patchInternalDraft(groupId, { selected });
            }}
            onPatchInternal={(groupId, patch) => {
              if (!createReviewPermissions.canEditInternal) return;
              patchInternalDraft(groupId, patch);
            }}
            onToggleExternal={(groupId, selected) => {
              if (!createReviewPermissions.canSelect) return;
              patchDraft(groupId, { selected });
            }}
            onPatchExternal={(groupId, patch) => {
              if (!createReviewPermissions.canEditExternal) return;
              patchDraft(groupId, patch);
              if (patch.customFieldValues != null || patch.customFields != null) {
                setError(null);
              }
            }}
            onAddInternal={(group) => {
              if (!createReviewPermissions.canAdd) return undefined;
              return addInternalToReview(group);
            }}
            onAddExternal={(group) => {
              if (!createReviewPermissions.canAdd) return undefined;
              return addExternalToReview(group);
            }}
            onConvertInternalToExternal={(group) => {
              if (!createReviewPermissions.canEditInternal) return;
              convertInternalToExternal(group);
            }}
            onInternalSettings={(group, patch) => {
              if (!createReviewPermissions.canEditInternal) return;
              void changesApi.updateWorkGroup(group.group_id, patch).then((updated) => {
                setWorkGroups((prev) =>
                  prev.map((item) => (item.group_id === group.group_id ? { ...item, ...updated } : item)),
                );
              }).catch((err: unknown) => {
                setError(getApiErrorMessage(err, 'Não foi possível atualizar a área.'));
              });
            }}
            onRemoveInternal={(reviewKey) => {
              if (!createReviewPermissions.canRemove) return;
              setReviewInternalKeys((prev) => prev.filter((key) => key !== reviewKey));
            }}
            onRemoveExternal={(lineKey) => {
              if (!createReviewPermissions.canRemove) return;
              const groupId = parseExternalLineKey(lineKey).groupId;
              if (!groupId) return;
              setReviewExternalIds((prev) => prev.filter((id) => id !== groupId));
              setDrafts((prev) => {
                if (!prev[groupId]) return prev;
                const next = { ...prev };
                delete next[groupId];
                return next;
              });
            }}
            onPanelChange={(editing) => {
              setReviewEditing(editing);
            }}
            allowInternalSubtasks={subtaskPolicy.enable_internal_subtasks}
            instancePackageMode={subtaskPolicy.enable_internal_subtasks}
            reviewPermissions={createReviewPermissions}
            templatePersistMode={templatePersistMode}
            onTemplateSaved={handleTemplateSaved}
            onTemplateDeleted={handleTemplateDeleted}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 2 ? (
          <Button variant="outline-secondary" onClick={() => setStep(1)} disabled={saving}>
            Voltar
          </Button>
        ) : (
          <Button variant="outline-secondary" onClick={onHide}>
            Cancelar
          </Button>
        )}
        {step === 2 && reviewEditing ? (
          <span className="me-auto small text-muted">Volte à lista para confirmar a criação.</span>
        ) : step === 2 && selectedReviewCount === 0 ? (
          <span className="me-auto small text-muted">Só a mudança será criada.</span>
        ) : null}
        <Button
          onClick={() => void handlePrimary()}
          disabled={saving || !groupsReady || (step === 2 && reviewEditing)}
        >
          {saving
            ? 'Salvando...'
            : step === 1
              ? hasReviewItems
                ? 'Continuar'
                : 'Criar mudança'
              : selectedReviewCount
                ? `Confirmar criação (${selectedReviewCount} ${selectedReviewCount === 1 ? 'item' : 'itens'})`
                : 'Confirmar criação'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeCreateModal;
